import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { EvcAuthService } from '@core/services/evc-auth.service';
import { platform } from 'src/platforms/platform';
import { OscPlatform } from '@osc-utils/enums/osc-platform';
import endpointConfig from 'src/endpoints-config/endpoints-config.json';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  #authService = inject(EvcAuthService);

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (!this.isAuthHeaderRequired(request)) {
      return next.handle(request);
    }
    const originalToken = this.#authService.getIdTokenValue();
    return next.handle(this.includeAuthHeader(request)).pipe(
      catchError(error => {
        if (!(error instanceof HttpErrorResponse)) {
          return throwError(() => error);
        }
        if (error.status !== 401) {
          return throwError(() => error);
        }

        const currentToken = this.#authService.getIdTokenValue();
        return originalToken === currentToken
          ? this.refreshToken().pipe(switchMap(() => next.handle(this.includeAuthHeader(request))))
          : next.handle(this.includeAuthHeader(request)); // The token has already been refreshed, retry with the new one.
      }),
    );
  }

  private refreshToken(): Observable<any> {
    return this.#authService.refreshTokens().pipe(
      catchError(error => {
        if (platform.name === OscPlatform.Web) {
          this.#authService.login(); // On mobile we always want the user to initiate the login.
        }
        return throwError(() => error);
      }),
    );
  }

  private includeAuthHeader(request: HttpRequest<any>): HttpRequest<any> {
    const accessToken = this.#authService.getIdTokenValue();
    if (!accessToken) {
      return request;
    }
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  private isAuthHeaderRequired(request: HttpRequest<any>): boolean {
    return request.url.startsWith(endpointConfig.webApiUrl);
  }
}
